/**
 * ChipRadioGroup: a single-select row of chips (#2434, #2422 step 5).
 *
 * The contact, medication, symptom and question forms drew their chip groups
 * as plain buttons inside a wrapping `<label>`. Two defects followed from that
 * shape. A tap on the heading text activated the label's first control, so
 * tapping "Relationship" silently moved the choice to Family. And the first
 * chip took the whole label's text as its accessible name, so a screen reader
 * announced the Family chip as the list of every other option, while no chip
 * announced whether it was the chosen one.
 *
 * This is Radix RadioGroup styled as chips. Each chip is `role="radio"` with
 * `aria-checked` and is named by its own text. The group is named by the
 * heading through `aria-labelledby`, and the heading is never a `<label>`, so
 * tapping it does nothing. Arrow keys move the choice, as a radio group's do.
 *
 * Multi-select chip rows (the medication time-of-day slots) are toggle
 * buttons with `aria-pressed`, not this.
 */
import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';

export interface ChipRadioOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** A colour class for a small leading dot (e.g. `bg-status-ok-fg`). The dot
   *  is decoration: the chip's text is its name. */
  markerClassName?: string;
}

export interface ChipRadioGroupProps<T extends string> {
  /** The id of the visible heading that names the group. */
  labelledBy: string;
  value: T;
  onValueChange: (value: T) => void;
  options: readonly ChipRadioOption<T>[];
  /** Layout of the row, e.g. `grid grid-cols-3 gap-2`. */
  className?: string;
  /** Extra classes for every chip (size, alignment). */
  chipClassName?: string;
  /** Extra classes for the marker dot (size). */
  markerClassName?: string;
  disabled?: boolean;
}

export function ChipRadioGroup<T extends string>({
  labelledBy,
  value,
  onValueChange,
  options,
  className,
  chipClassName,
  markerClassName,
  disabled,
}: ChipRadioGroupProps<T>) {
  return (
    <RadioGroupPrimitive.Root
      aria-labelledby={labelledBy}
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      disabled={disabled}
      className={cn('grid grid-cols-3 gap-2', className)}
    >
      {options.map((o) => (
        <RadioGroupPrimitive.Item
          key={o.value}
          value={o.value}
          className={cn(
            'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-caption text-start transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[state=checked]:border-stevens-indigo data-[state=checked]:bg-stevens-indigo/5 data-[state=checked]:text-foreground',
            'data-[state=unchecked]:border-border data-[state=unchecked]:hover:bg-muted',
            chipClassName,
          )}
        >
          {o.markerClassName ? (
            <span aria-hidden="true" className={cn('h-[7px] w-[7px] shrink-0 rounded-full', markerClassName, o.markerClassName)} />
          ) : null}
          {o.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
